import { Breadcrumbs } from '@realtyfeed/ui';
import { useEffect, type ReactNode } from 'react';
import { useMatches } from 'react-router-dom';
import type { ShellRouteHandle } from '../type';

const isShellRouteHandle = (handle: unknown): handle is ShellRouteHandle =>
  typeof handle === 'object' &&
  handle !== null &&
  typeof (handle as { title?: unknown }).title === 'string';

/** The deepest matched route that declares a handle — the screen actually on show. */
const useCurrentHandle = (): ShellRouteHandle | undefined => {
  const matches = useMatches();
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const handle = matches[index]?.handle;
    if (isShellRouteHandle(handle)) return handle;
  }
  return undefined;
};

/**
 * Reads the route table's `handle` metadata and turns it into chrome.
 *
 * The legacy router carried `breadCrumb` and `hasBack` on each route and the Vuexy layout drew
 * them. The route table here declares the same data as `handle` (`src/app/config/routes.tsx`),
 * and this is what reads it:
 *
 * - **The document title**, on every route: `<title> · <suffix>`. The legacy left the tab
 *   reading the app's name on every screen, so twenty open tabs were indistinguishable.
 * - **The header, on every route**: the crumb trail (Dashboard, any `crumbs`, the screen), then
 *   the page title under it — the stacked `Breadcrumbs` the MLS vendor portal puts above every
 *   screen's card. A list's card therefore carries no title of its own. `hasBack` adds the back
 *   arrow beside the title on detail screens.
 *
 * Uses `useMatches`, so it must render inside a data router (`createBrowserRouter`).
 */
export const RouteHeader = ({ titleSuffix }: { titleSuffix: string }): ReactNode => {
  const handle = useCurrentHandle();
  const title = handle?.title;

  useEffect(() => {
    document.title = title ? `${title} · ${titleSuffix}` : titleSuffix;
  }, [title, titleSuffix]);

  if (!handle) return null;

  return (
    <Breadcrumbs
      title={handle.title}
      hasBack={handle.hasBack}
      className="mb-6"
      items={[
        { title: 'Dashboard', link: '/dashboard' },
        ...(handle.crumbs ?? []).map((crumb) => ({ title: crumb.title, link: crumb.to })),
        { title: handle.title },
      ]}
    />
  );
};
